import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../api';
import { DealPicker, type Deal } from '../DealPicker';
import { dealLabel } from '../lib/dealLabel';
import { keyToNoonIso, torontoDayKey } from '../lib/week';

// Tim's final feedback item 2: Fred bills ~45 min across many jobs, and
// clicking through the timesheet per job is slow. Split a day: pick the
// date, type the day's total hours, tap every job touched — the total is
// divided evenly and one entry per job is created. A multi-select beats
// the "write job numbers in the note and parse them" idea: same speed,
// no guess-the-job failure mode, and the entries are ordinary rows that
// edit/delete like any other.

interface SplitDayProps {
  employeeId: string;
  deals: Deal[];
}

const MAX_SPLIT_JOBS = 20;

// Divide total minutes across n jobs so the per-job values sum exactly to
// the total: the first (total % n) jobs get one extra minute.
export function splitMinutes(totalMinutes: number, n: number): number[] {
  const base = Math.floor(totalMinutes / n);
  const extra = totalMinutes % n;
  return Array.from({ length: n }, (_, i) => base + (i < extra ? 1 : 0));
}

export function SplitDay({ employeeId, deals }: SplitDayProps): JSX.Element {
  const qc = useQueryClient();
  const [open, setOpen] = useState<boolean>(false);
  const [date, setDate] = useState<string>(() => torontoDayKey(new Date()));
  const [hoursAmount, setHoursAmount] = useState<string>('');
  const [note, setNote] = useState<string>('');
  const [jobIds, setJobIds] = useState<string[]>([]);

  const hoursParsed = Number(hoursAmount);
  const hoursValid = Number.isFinite(hoursParsed) && hoursParsed > 0 && hoursParsed <= 24;
  const selectedDeals = useMemo(
    () => jobIds.map((id) => deals.find((d) => d.id === id)).filter((d): d is Deal => Boolean(d)),
    [jobIds, deals],
  );

  const addJob = (dealId: string): void => {
    setJobIds((prev) =>
      prev.includes(dealId) || prev.length >= MAX_SPLIT_JOBS ? prev : [...prev, dealId],
    );
  };
  const removeJob = (dealId: string): void => {
    setJobIds((prev) => prev.filter((id) => id !== dealId));
  };

  const perJobPreview = hoursValid && jobIds.length > 0
    ? (hoursParsed / jobIds.length).toFixed(2).replace(/\.?0+$/, '')
    : null;

  const submit = useMutation({
    mutationFn: async () => {
      const totalMinutes = Math.round(hoursParsed * 60);
      const shares = splitMinutes(totalMinutes, jobIds.length);
      const startedAt = keyToNoonIso(date);
      // Sequential POSTs: N is small (<=20) and ordering keeps the server's
      // audit trail readable. A failure mid-way leaves earlier entries in
      // place — surfaced below so the user can finish or clean up via the
      // week grid (entries are ordinary rows, individually deletable).
      let created = 0;
      for (const [i, id] of jobIds.entries()) {
        await apiFetch<{ id: string }>('/time-entries', {
          method: 'POST',
          body: JSON.stringify({
            dealId: id,
            billingType: 'hourly',
            minutes: shares[i],
            startedAt,
            ...(note.trim() ? { note: note.trim() } : {}),
          }),
        });
        created += 1;
      }
      return created;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      qc.invalidateQueries({ queryKey: ['week'] });
      qc.invalidateQueries({ queryKey: ['recent', employeeId] });
      qc.invalidateQueries({ queryKey: ['admin', 'jobs-summary'] });
      qc.invalidateQueries({ queryKey: ['admin', 'billing-summary'] });
      setHoursAmount('');
      setNote('');
      setJobIds([]);
      setDate(torontoDayKey(new Date()));
    },
  });

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex min-h-[44px] items-center justify-between rounded-lg bg-brand-steel px-3 py-2 text-sm font-medium text-brand-charcoal ring-1 ring-brand-steel hover:bg-brand-mist"
      >
        <span>Split a day across jobs</span>
        <span aria-hidden>＋</span>
      </button>
    );
  }

  return (
    <section className="flex flex-col gap-3 rounded-xl bg-brand-white p-4 ring-1 ring-brand-steel">
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col">
          <h2 className="text-sm font-semibold text-brand-charcoal">Split a day across jobs</h2>
          <p className="text-xs text-brand-slate">
            Total hours divide evenly over every job you pick.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label="Close"
          className="shrink-0 rounded-lg px-2 py-1 text-xs text-brand-slate ring-1 ring-brand-steel hover:bg-brand-steel"
        >
          Close
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-xs font-semibold uppercase tracking-wide text-brand-charcoal">Date</span>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="min-h-[44px] w-full min-w-0 rounded-lg bg-brand-white px-3 py-2 text-base text-brand-charcoal ring-1 ring-brand-steel focus:ring-2 focus:ring-brand-blue"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-semibold uppercase tracking-wide text-brand-charcoal">Total hours</span>
          <input
            type="number"
            inputMode="decimal"
            min={0.25}
            max={24}
            step={0.25}
            value={hoursAmount}
            onChange={(e) => setHoursAmount(e.target.value)}
            placeholder="e.g. 8"
            className="min-h-[44px] w-full min-w-0 rounded-lg bg-brand-white px-3 py-2 text-base text-brand-charcoal ring-1 ring-brand-steel focus:ring-2 focus:ring-brand-blue"
          />
        </label>
      </div>

      <div className="flex flex-col gap-1">
        <span className="text-xs font-semibold uppercase tracking-wide text-brand-charcoal">
          Add jobs ({jobIds.length})
        </span>
        <DealPicker deals={deals} value={null} onChange={addJob} />
      </div>

      {selectedDeals.length > 0 && (
        <ul className="flex flex-col gap-1.5">
          {selectedDeals.map((d) => (
            <li
              key={d.id}
              className="flex items-center justify-between gap-2 rounded-lg bg-brand-mist px-3 py-1.5 ring-1 ring-brand-steel"
            >
              <span className="min-w-0 truncate text-sm text-brand-charcoal">{dealLabel(d)}</span>
              <span className="flex shrink-0 items-center gap-2">
                {perJobPreview && (
                  <span className="text-xs tabular-nums text-brand-slate">≈{perJobPreview}h</span>
                )}
                <button
                  type="button"
                  onClick={() => removeJob(d.id)}
                  aria-label={`Remove ${dealLabel(d)}`}
                  className="rounded-md px-1.5 py-0.5 text-xs text-red-600 hover:bg-red-50"
                >
                  ✕
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}

      <label className="flex flex-col gap-1">
        <span className="text-xs font-semibold uppercase tracking-wide text-brand-charcoal">Note (optional, applied to every job)</span>
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. site visits + reviews"
          className="min-h-[44px] rounded-lg bg-brand-white px-3 py-2 text-base text-brand-charcoal ring-1 ring-brand-steel focus:ring-2 focus:ring-brand-blue"
        />
      </label>

      {hoursAmount && !hoursValid && (
        <p className="text-sm text-red-600">Total hours must be between 0.25 and 24.</p>
      )}

      <button
        type="button"
        onClick={() => submit.mutate()}
        disabled={submit.isPending || !date || !hoursValid || jobIds.length === 0}
        className="min-h-[56px] rounded-xl bg-brand-blue px-6 py-4 text-base font-semibold text-brand-white hover:bg-[#0067BB] active:scale-[0.98] transition-transform disabled:opacity-50"
      >
        {submit.isPending
          ? 'Logging…'
          : jobIds.length > 0 && hoursValid
            ? `Split ${hoursParsed}h across ${jobIds.length} ${jobIds.length === 1 ? 'job' : 'jobs'}`
            : 'Split hours'}
      </button>

      {submit.isError && (
        <p className="text-sm text-red-600">
          Couldn&rsquo;t log every job:{' '}
          {submit.error instanceof Error ? submit.error.message : 'Unknown error.'}{' '}
          Check the week grid — some entries may already be in; delete or retry the rest.
        </p>
      )}
      {submit.isSuccess && (
        <p className="text-sm text-emerald-700">
          Logged {submit.data} {submit.data === 1 ? 'entry' : 'entries'}.
        </p>
      )}
    </section>
  );
}
